import React, { useEffect, useState, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { handleBuyNow } from "../../checkout/api";
import { getProductBySlug } from "../api";
import DOMPurify from "dompurify";
import { S3_URL_PRODUCTS, DEFAULT_VENDOR_CODE } from "../../../shared/utils/constants";
import { useCart } from "../../cart/context/CartContext";
import { toast } from "react-toastify";
import { AuthContext } from "../../auth/context/AuthContext";
import { addToWishlist } from "../../wishlist/api";
import SimilarProducts from "./SimilarProducts";
import kokoImage from "../../../assets/images/kokoImage.png";
import Breadcrumbs from "./Breadcrumbs";
import { getBreadcrumbByProductId } from "../api";
import "../styles/product-details.css";

import { WhatsappLogo, FacebookLogo } from "@phosphor-icons/react";
import Loading from "../../../shared/ui/Loading";
import NoResults from "../../../shared/ui/NoResults";
import Seo, { toMetaDescription } from "../../../shared/ui/Seo";
import { SITE_URL } from "../../../shared/utils/constants";
const ProductDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [isWishlistSubmitting, setIsWishlistSubmitting] = useState(false);

  // State declarations.
  const [product, setProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [productImages, setProductImages] = useState([]);
  const [mainImage, setMainImage] = useState([]);
  const [price, setPrice] = useState(0);
  const { addToCart } = useCart();
  const [attributeSelections, setAttributeSelections] = useState({});
  const [variation, setVariation] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [shortDescription, setShortDescription] = useState("");
  const [breadcrumbs, setBreadcrumbs] = useState({
    category: null,
    subcategory: null,
  });

  // Define display variables early so that all hooks and render methods can use them.
  // displayProduct: used for pricing/SKU; displayMainProduct: used for descriptions.
  const displayProduct =
    product?.parentProduct &&
    product.parentProduct.type === "variable" &&
    variation
      ? variation.product
      : product?.parentProduct?.product || null;
  const displayMainProduct =
    product?.parentProduct &&
    product.parentProduct.type === "variable" &&
    variation
      ? variation
      : product?.parentProduct || null;

  // Fetch product details (parentProduct and variations come together).
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await getProductBySlug(slug);
        // console.log("Product data:", data);
        if (!data || !data.parentProduct) {
          throw new Error("Product not found");
        }
        setProduct(data);
        console.log(data);
        const productId = data?.parentProduct?.product?.id;
        if (productId) {
          fetchBreadcrumbDetails(productId);
        }

        // Set shortDescription from parent product data.
        setShortDescription(data.parentProduct.shortDescription);
        // Set images.
        setProductImages(
          data.parentProduct.product.images.map(
            (image) => `${S3_URL_PRODUCTS}${image}`,
          ),
        );
        setMainImage(
          `${S3_URL_PRODUCTS}${data.parentProduct.product.images[0]}`,
        );
      } catch (error) {
        console.error("Error fetching product details:", error);
      } finally {
        setLoading(false);
      }
    };
    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  // Takes productId as input and talks to the backend to get the breadcrumb info
  const fetchBreadcrumbDetails = async (productId) => {
    try {
      console.log("Fetching breadcrumbs for product ID:", productId);

      const data = await getBreadcrumbByProductId(productId);

      const subcategoryName = Object.keys(data)[0];
      const subcategoryData = data[subcategoryName];

      if (subcategoryData && subcategoryData.mainCategory) {
        // The API links are "/<categoryId>"; both open the filterable products page.
        const categoryId = subcategoryData.mainCategory.link.replace(/^\//, "");
        const subcategoryId = subcategoryData.link.replace(/^\//, "");
        setBreadcrumbs({
          category: {
            name: subcategoryData.mainCategory.name,
            link: `/products?category=${categoryId}`,
          },
          subcategory: {
            name: subcategoryName,
            link: `/products?category=${categoryId}&sub=${subcategoryId}`,
          },
        });
        console.log("Breadcrumbs set:", data);
      } else {
        console.warn("Invalid breadcrumb structure:", data);
      }
    } catch (error) {
      console.error("Error fetching breadcrumb details:", error);
    }
  };

  // Calculate price using displayProduct's price.
  // Price Calculation (e.g., in a component file)
  useEffect(() => {
    if (displayProduct) {
      const config = product?.parentProduct?.product?.configuration;
      const configDiscount =
        config?.Sale?.collectionConfiguration?.discount || 0;
      const discountActive =
        configDiscount && config?.Sale?.configStatus === "active";

      let computedSalePrice;
      if (discountActive) {
        if (displayProduct.salePrice) {
          // Case 1: Config is active, and salePrice exists.
          // Apply the discount on the salePrice.
          computedSalePrice =
            displayProduct.salePrice * (1 - configDiscount / 100);
        } else {
          // Case 3: Config active but only regularPrice exists.
          computedSalePrice =
            displayProduct.regularPrice * (1 - configDiscount / 100);
        }
      } else {
        // Case 2 and Case 4:
        // Use the provided salePrice if available; otherwise, fall back to regularPrice.
        computedSalePrice =
          displayProduct.salePrice || displayProduct.regularPrice;
      }

      const effectiveUnitPrice = computedSalePrice;
      setPrice(effectiveUnitPrice * quantity);
    }
  }, [quantity, displayProduct, product]);

  // For variable products, compute matching variation based on attribute selections.
  useEffect(() => {
    if (
      !product ||
      !product.parentProduct ||
      product.parentProduct.type !== "variable"
    )
      return;
    // Clear variation if no attribute is selected.
    if (Object.keys(attributeSelections).length === 0) {
      setVariation(null);
      return;
    }
    const matches = product.variations.filter((v) =>
      Object.keys(product.parentProduct.attributes).every((key) => {
        if (!attributeSelections[key]) return true;
        return (
          v.attributes[key]?.options &&
          v.attributes[key].options[0] === attributeSelections[key]
        );
      }),
    );
    if (matches.length === 1) {
      setVariation(matches[0]);
      setMainImage(`${S3_URL_PRODUCTS}${matches[0].product.images[0]}`);
    } else {
      setVariation(null);
    }
  }, [attributeSelections, product]);

  // Helper: compute available options for an attribute.
  const getAvailableOptions = (attrKey) => {
    const fullOptions =
      product.parentProduct?.attributes[attrKey]?.options || [];
    if (product.parentProduct.type !== "variable" || !product.variations.length)
      return fullOptions;
    const filtered = product.variations.filter((v) => {
      return Object.keys(product.parentProduct.attributes).every((key) => {
        if (key === attrKey) return true;
        const selected = attributeSelections[key];
        if (!selected) return true;
        return (
          v.attributes[key]?.options &&
          v.attributes[key].options[0] === selected
        );
      });
    });
    const optionsSet = new Set();
    filtered.forEach((v) => {
      if (v.attributes[attrKey]?.options) {
        optionsSet.add(v.attributes[attrKey].options[0]);
      }
    });
    return optionsSet.size > 0 ? Array.from(optionsSet) : fullOptions;
  };

  const incrementQuantity = () => setQuantity((prev) => prev + 1);
  const decrementQuantity = () =>
    setQuantity((prev) => (prev > 1 ? prev - 1 : prev));

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (
      product.parentProduct.type === "variable" &&
      (Object.keys(attributeSelections).length === 0 || !variation)
    ) {
      toast.error("Please select all options before adding to cart.");
      return;
    }
    const productId =
      product.parentProduct.type === "variable"
        ? variation.product.id
        : product.parentProduct.product.id;
    addToCart({ id: productId, quantity, slug });
  };

  const handleBuyNowFunc = (e) => {
    e.preventDefault();
    if (
      product.parentProduct.type === "variable" &&
      (Object.keys(attributeSelections).length === 0 || !variation)
    ) {
      toast.error("Please select all options before proceeding to checkout.");
      return;
    }
    const productToBuy =
      product.parentProduct.type === "variable"
        ? variation.product
        : product.parentProduct.product;
    handleBuyNow({ ...productToBuy, quantity });
    navigate("/checkout");
  };

  const settingsThumbs = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    focusOnSelect: true,
  };
  const handleAddToWishlist = async () => {
    if (
      product.parentProduct.type === "variable" &&
      (Object.keys(attributeSelections).length === 0 || !variation)
    ) {
      toast.error("Please select all options before adding to cart.");
      return;
    }
    const productId =
      product.parentProduct.type === "variable"
        ? variation.product.id
        : product.parentProduct.product.id;
    if (isWishlistSubmitting) {
      return;
    }
    setIsWishlistSubmitting(true);
    try {
      await addToWishlist(productId);
      window.dispatchEvent(new Event("wishlistUpdated"));
      toast.success("Item added to wishlist");
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data || "An unexpected error occurred";
      toast.error(`Error adding to wishlist: ${errorMessage}`);
    } finally {
      setIsWishlistSubmitting(false);
    }
  };
  const getCategoryId = (categories) => {
    return (
      (categories && categories[1]?.id) ||
      (categories && categories[0]?.id) ||
      "b0f9c584-5a54-4a1e-b925-1fbd1a4a9a07"
    );
  };
  const categoryIdToPass = getCategoryId(
    product?.parentProduct?.product?.category,
  );
  // Extract discount and status from product configuration

  if (loading) return <Loading message="Loading product details..." />;
  if (!product || !product.parentProduct)
    return (
      <>
        <Seo title="Product Not Found" noindex />
        <NoResults message="Product not found" />
      </>
    );
  const config = product?.parentProduct?.product?.configuration;
  const configDiscount = config?.Sale?.collectionConfiguration?.discount || 0;
  const discountActive =
    configDiscount && config?.Sale?.configStatus === "active";

  let computedSalePrice;
  if (discountActive) {
    if (displayProduct.salePrice) {
      // Case 1
      computedSalePrice = displayProduct.salePrice * (1 - configDiscount / 100);
    } else {
      // Case 3
      computedSalePrice =
        displayProduct.regularPrice * (1 - configDiscount / 100);
    }
  } else {
    // Case 2 & 4
    computedSalePrice = displayProduct.salePrice || displayProduct.regularPrice;
  }

  // Determine if a discount badge should be shown.
  const shouldShowDiscount = discountActive || displayProduct.salePrice;
  // Calculate discount percentage based on the regular price.
  const discountPercentage = shouldShowDiscount
    ? Math.round(
        ((displayProduct.regularPrice - computedSalePrice) /
          displayProduct.regularPrice) *
          100,
      )
    : 0;

  const productPath = `/product-details/${slug}`;
  const metaDescription =
    toMetaDescription(shortDescription) ||
    `Buy ${displayProduct?.name} online at Bigmall for LKR ${Math.round(computedSalePrice).toLocaleString()} with island-wide delivery in Sri Lanka.`;
  const breadcrumbItems = [
    { name: "Home", path: "/" },
    breadcrumbs.category,
    breadcrumbs.subcategory,
    { name: displayProduct?.name, path: productPath },
  ]
    .filter(Boolean)
    .map((item) => ({ name: item.name, path: item.path || item.link }));
  const productJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: displayProduct?.name,
      description: toMetaDescription(shortDescription, 5000) || undefined,
      image: productImages,
      sku: displayProduct?.sku || undefined,
      url: `${SITE_URL}${productPath}`,
      offers: {
        "@type": "Offer",
        url: `${SITE_URL}${productPath}`,
        priceCurrency: "LKR",
        price: Number(computedSalePrice).toFixed(2),
        availability:
          displayProduct?.stockStatus === "outofstock"
            ? "https://schema.org/OutOfStock"
            : "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: { "@type": "Organization", name: "Bigmall" },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: `${SITE_URL}${item.path}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={displayProduct?.name}
        description={metaDescription}
        path={productPath}
        image={mainImage || undefined}
        type="product"
        jsonLd={productJsonLd}
      />

      <div className="container container-lg">
        {product && (
          <Breadcrumbs
            category={breadcrumbs.category}
            subcategory={breadcrumbs.subcategory}
            productName={displayProduct?.name}
          />
        )}
        <section className="product-details mt-16">
          <div className="row gy-4">
            <div className="col-xl-9">
              <div className="row gy-4">
                <div className="col-xl-6">
                  <div className="product-details__left">
                    <img src={mainImage} alt={displayProduct?.name} />
                    <div className="mt-24">
                      <div className="product-details__images-slider">
                        <Slider {...settingsThumbs}>
                          {productImages.map((image, index) => (
                            <div
                              key={index}
                              className="center max-w-120 max-h-120 h-100 flex-center border border-gray-100 rounded-16 p-8"
                              onClick={() => setMainImage(image)}
                            >
                              <img
                                className="thum"
                                src={image}
                                alt={`${displayProduct?.name} - view ${index + 1}`}
                              />
                            </div>
                          ))}
                        </Slider>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-6">
                  <div className="product-details__content">
                    <h1 className="h5 mb-12">{displayProduct?.name}</h1>

                    {product.parentProduct.product.nonCod && (
                      <div className="cod-status">
                        <button type="button" className="cod-status__trigger">
                          <span className="cod-status__icon">
                            <i className="ph-fill ph-info" />
                          </span>
                          <span className="cod-status__label">
                            Cash on Delivery not available
                          </span>
                        </button>

                        <div className="cod-status__popover">
                          Cash on Delivery (COD) is not available for this item. You can still add it to your cart, but orders containing it must be paid online.
                        </div>
                      </div>
                    )}
                    <div className="flex-align gap-8">
                      {product.parentProduct.product.configuration &&
                        product.parentProduct.product.configuration?.Sale
                          ?.configStatus == "active" && (
                          <Link
                            to={`/${product.parentProduct.product.configuration?.Sale?.type?.toLowerCase()}/${
                              product.parentProduct.product.configuration.Sale
                                .slug
                            }`}
                          >
                            <div className="product-card__badge mb-16">
                              <span className="badge bg-main-50 text-main-600">
                                {
                                  product.parentProduct.product.configuration
                                    .Sale.collectionDisplayName
                                }
                              </span>
                            </div>
                          </Link>
                        )}
                      {product.parentProduct.product.configuration &&
                        product.parentProduct.product.configuration
                          ?.Free_Delivery?.configStatus == "active" && (
                          <Link
                            to={`/${product.parentProduct.product.configuration?.Free_Delivery?.type?.toLowerCase()}/${
                              product.parentProduct.product.configuration
                                .Free_Delivery.slug
                            }`}
                          >
                            <div className="product-card__badge mb-16">
                              <span className="badge bg-success-50 text-success-600">
                                {
                                  product.parentProduct.product.configuration
                                    .Free_Delivery.collectionDisplayName
                                }
                                <i className="ph ph-truck" />
                              </span>
                            </div>
                          </Link>
                        )}
                    </div>

                    <div className="flex-align flex-wrap gap-12">
                      <span className="text-sm fw-medium text-gray-500">|</span>
                      <span className="text-gray-900">
                        <span className="text-gray-400">SKU:</span>{" "}
                        {displayProduct?.sku}
                      </span>
                    </div>
                    {/* Display categories */}
                    <div className="flex-align gap-8 my-16">
                      <span className="text-sm text-neutral-600">
                        Categories:
                      </span>
                      <div className="flex gap-4 flex-wrap">
                        {product.parentProduct.product.category &&
                        product.parentProduct.product.category.length > 0 ? (
                          product.parentProduct.product.category.map(
                            (cat, index) => (
                              <React.Fragment key={cat.id}>
                                <Link
                                  to={`/products?category=${cat.id}`}
                                  className="text-blue-600 hover:text-blue-800 transition-colors text-sm fw-medium"
                                >
                                  {cat.name}
                                </Link>
                                {index <
                                  product.parentProduct.product.category
                                    .length -
                                    1 && ", "}
                              </React.Fragment>
                            ),
                          )
                        ) : (
                          <span className="text-gray-400">
                            No categories available
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                    <p className="text-gray-700">
                      {shortDescription && shortDescription.includes("<") ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(shortDescription),
                          }}
                        />
                      ) : (
                        shortDescription
                      )}
                    </p>
                    <div className="my-5 d-flex flex-column gap-16 flex-wrap">
                      {shouldShowDiscount ? (
                        <>
                          <div className="flex-align gap-8 mt-4">
                            <span className="text-gray-700">Regular Price</span>
                            <h6 className="text-xl text-gray-400 mb-0 fw-medium text-decoration-line-through">
                              LKR {displayProduct.regularPrice.toFixed(2)}
                            </h6>
                          </div>
                          <div className="flex-align gap-8">
                            <h6 className="mb-0">
                              Offer Price LKR {computedSalePrice.toFixed(2)}
                            </h6>
                          </div>
                          <div className="flex-align gap-8 text-main-two-600">
                            <i className="ph-fill ph-seal-percent text-xl" />-
                            {discountPercentage}%
                          </div>
                        </>
                      ) : (
                        <div className="flex-align gap-8">
                          <h6 className="mb-0">
                            LKR {displayProduct?.regularPrice?.toFixed(2)}
                          </h6>
                        </div>
                      )}
                    </div>

                    {/* Render attribute dropdowns only for variable products */}
                    {product.parentProduct.type === "variable" &&
                      product.parentProduct.attributes &&
                      Object.keys(product.parentProduct.attributes).length >
                        0 && (
                        <div className="mb-32">
                          <h6 className="mb-16">Select Options:</h6>
                          {Object.keys(product.parentProduct.attributes).map(
                            (attrKey) => (
                              <div key={attrKey} className="mb-16">
                                <label
                                  htmlFor={attrKey}
                                  className="fw-medium text-neutral-600"
                                >
                                  {attrKey.charAt(0).toUpperCase() +
                                    attrKey.slice(1)}
                                </label>
                                <select
                                  id={attrKey}
                                  className="form-select"
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setAttributeSelections((prev) => ({
                                      ...prev,
                                      [attrKey]: value,
                                    }));
                                  }}
                                  value={attributeSelections[attrKey] || ""}
                                >
                                  <option value="">Select {attrKey}</option>
                                  {getAvailableOptions(attrKey).map(
                                    (option, index) => (
                                      <option key={index} value={option}>
                                        {option}
                                      </option>
                                    ),
                                  )}
                                </select>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3">
              <div className="product-details__sidebar py-10 px-32 border border-gray-100 rounded-16">
                <div className="mb-20">
                  <span className="text-xl d-flex">
                    <i className="ph ph-location" />
                  </span>

                  <div className="d-flex rounded-4 overflow-hidden">
                    <button
                      onClick={decrementQuantity}
                      type="button"
                      className="quantity__minus flex-shrink-0 h-48 w-48 text-neutral-600 bg-gray-50 flex-center hover-bg-main-600 hover-text-white"
                    >
                      <i className="ph ph-minus" />
                    </button>
                    <input
                      type="number"
                      className="quantity__input flex-grow-1 border border-gray-100 border-start-0 border-end-0 text-center w-32 px-16"
                      value={quantity}
                      readOnly
                    />
                    <button
                      onClick={incrementQuantity}
                      type="button"
                      className="quantity__plus flex-shrink-0 h-48 w-48 text-neutral-600 bg-gray-50 flex-center hover-bg-main-600 hover-text-white"
                    >
                      <i className="ph ph-plus" />
                    </button>
                  </div>
                </div>
                <div className="mb-20">
                  <div className="flex-between flex-wrap gap-8 border-bottom border-gray-100 pb-16 mb-16">
                    <span className="text-gray-500">Price</span>
                    <h6 className="text-lg mb-0">LKR {price.toFixed(2)}</h6>
                  </div>
                </div>
                <button
                    className={`btn btn-main flex-center gap-8 rounded-8 py-16 fw-normal w-100 ${
                      product.parentProduct.type === "variable" && !variation
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleAddToCart}
                    disabled={
                      product.parentProduct.type === "variable" &&
                      Object.keys(product.parentProduct.attributes).length >
                        0 &&
                      (Object.keys(attributeSelections).length <
                        Object.keys(product.parentProduct.attributes).length ||
                        !variation)
                    }
                  >
                    <i className="ph ph-shopping-cart-simple" />
                    Add to Cart
                  </button>
                {isAuthenticated && !product.parentProduct.product.nonCod && (
                  <button
                    className={`btn btn-main flex-center gap-8 rounded-8 py-16 fw-normal mt-16 w-100 ${
                      product.parentProduct.type === "variable" && !variation
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleAddToWishlist}
                    disabled={
                      (product.parentProduct.type === "variable" &&
                        Object.keys(product.parentProduct.attributes).length >
                          0 &&
                        (Object.keys(attributeSelections).length <
                          Object.keys(product.parentProduct.attributes)
                            .length ||
                          !variation)) ||
                      isWishlistSubmitting
                    }
                  >
                    {isWishlistSubmitting ? (
                      <i className="ph ph-spinner animate-spin" /> // Loading icon
                    ) : (
                      <>
                        <i className="ph ph-heart" />
                        Add to Wishlist
                      </>
                    )}
                  </button>
                )}

                <button
                  className={`btn btn-outline-main rounded-8 py-16 fw-normal mt-16 w-100 ${
                    product.parentProduct.type === "variable" && !variation
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={handleBuyNowFunc}
                  disabled={
                    product.parentProduct.type === "variable" &&
                    Object.keys(product.parentProduct.attributes).length > 0 &&
                    (Object.keys(attributeSelections).length <
                      Object.keys(product.parentProduct.attributes).length ||
                      !variation)
                  }
                >
                  Buy Now
                </button>
                {product.parentProduct && product.parentProduct.warranty && (
                  <div className="mt-32">
                    <div className="px-16 rounded-8 flex-between gap-8 mb-0">
                      <span className="w-32 h-32 bg-white text-main-600 rounded-circle flex-center text-xl flex-shrink-0">
                        <i className="ph-fill ph-shield-check" />
                      </span>
                      <span className="text-sm text-neutral-600">
                        Warranty:{" "}
                        <strong className="fw-semibold   transition-colors">
                          {product.parentProduct.warranty}
                        </strong>
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-10">
                  <div className="px-16 rounded-8 flex-between gap-8 mb-0">
                    <span className="w-32 h-32 bg-white text-main-600 rounded-circle flex-center text-xl flex-shrink-0">
                      <i className="ph-fill ph-storefront" />
                    </span>
                    <span className="text-sm text-neutral-600">
                      Sold by:{" "}
                      {product?.parentProduct?.account?.id ? (
                        <Link
                          to={`/shop/${product.parentProduct.account.id}`}
                          className="fw-semibold text-dark hover-text-primary-800 transition-colors"
                        >
                          {product.parentProduct.account?.code
                            ? product.parentProduct.account.code
                            : DEFAULT_VENDOR_CODE}
                        </Link>
                      ) : (
                        <span className="fw-semibold">
                          {DEFAULT_VENDOR_CODE}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="mt-32">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {/* WhatsApp Icon Link */}
                    <a
                      href="https://wa.me/94777961772"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-success-600"
                    >
                      <WhatsappLogo size={32} />
                    </a>
                    {/* Facebook Messenger Icon Link */}
                    <a
                      href="https://www.facebook.com/bigmall.lk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600"
                    >
                      <FacebookLogo size={32} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-80">
            <div className="product-dContent border rounded-24">
              <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
                <ul
                  className="nav common-tab nav-pills mb-3"
                  id="pills-tab"
                  role="tablist"
                >
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="pills-description-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-description"
                      type="button"
                      role="tab"
                      aria-controls="pills-description"
                      aria-selected="true"
                    >
                      Description
                    </button>
                  </li>
                </ul>
              </div>
              <div className="product-dContent__box">
                <div className="tab-content" id="pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="pills-description"
                    role="tabpanel"
                    aria-labelledby="pills-description-tab"
                    tabIndex={0}
                  >
                    <div className="mb-40">
                      <h6 className="mb-24">Product Description</h6>

                      {displayMainProduct?.description &&
                      displayMainProduct.description.includes("<") ? (
                        <p
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              displayMainProduct.description,
                            ),
                          }}
                        />
                      ) : (
                        <p>{displayMainProduct?.description}</p>
                      )}

                      {/* Hardcoded Image */}
                      <img
                        src={kokoImage}
                        alt="Pay in 3 installments with Koko"
                        style={{
                          marginTop: "20px",
                          width: "100%",
                          maxWidth: "300px",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <SimilarProducts categoryId={categoryIdToPass} />
        </section>
      </div>
    </>
  );
};

export default ProductDetails;
